import { useState, type ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Activity, Battery, Cpu, MoreHorizontal, Plus, Search, Zap } from 'lucide-react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/common/components/ui/button';
import { Input } from '@/common/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/common/components/ui/dropdown-menu';
import { PageHeader } from '@/common/components/page-header/PageHeader';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { deviceQueries, deviceQueryKeys } from '@/features/devices/queries';
import { DeviceService } from '@/features/devices/services/DeviceService';
import type { Device } from '@/features/devices/services/schemas';

export function DevicesPage() {
  const [search, setSearch] = useState('');
  const { activeSupplyPoint } = useSupplyPoint();
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();

  const { data: devices } = useSuspenseQuery(
    deviceQueries.listByEnergySystem(activeSupplyPoint!.id)
  );
  const { data: deviceTypes } = useSuspenseQuery(deviceQueries.types());

  const deleteMutation = useMutation({
    mutationFn: (id: number) => DeviceService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all }),
  });

  const handleDelete = async (device: Device) => {
    const deviceName = device.name;
    const confirmed = await confirm({
      title: t`Remove device?`,
      description: t`${deviceName} and all its sub-devices will be permanently removed.`,
      confirmLabel: t`Remove`,
    });
    if (confirmed) deleteMutation.mutate(device.id);
  };

  const typeMap = Object.fromEntries(deviceTypes.map((t) => [t.id, t.name]));

  const lowerSearch = search.toLowerCase();

  // When searching, show matching devices flat (any level).
  // Without search, build the hierarchy starting from root devices.
  const isSearching = lowerSearch.length > 0;
  const filteredDevices = isSearching
    ? devices.filter((d) => d.name.toLowerCase().includes(lowerSearch))
    : devices;

  // Root devices: no parent, or their parent was removed by search filter
  const parents = isSearching ? filteredDevices : filteredDevices.filter((d) => d.parent === null);

  const getTypeIcon = (deviceTypeId: number) => {
    const name = typeMap[deviceTypeId]?.toLowerCase() ?? '';
    if (name.includes('meter')) return <Activity className="text-blue-500" size={16} />;
    if (name.includes('inverter')) return <Zap className="text-amber-500" size={16} />;
    if (name.includes('battery')) return <Battery className="text-green-500" size={16} />;
    return <Cpu className="text-purple-500" size={16} />;
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={<Trans>Devices</Trans>}
        subtitle={<Trans>Manage and monitor hardware connected to this supply point.</Trans>}
        actions={
          <Button size="sm" onClick={() => navigate({ to: '/devices/new' })}>
            <Plus size={16} />
            <Trans>Add device</Trans>
          </Button>
        }
      />

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {/* Search bar */}
        <div className="flex items-center gap-4 px-4 py-3 border-b border-border">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder={t`Search devices...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Trans>Name</Trans>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Trans>Type</Trans>
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {parents.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-16 text-center">
                    <Cpu size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">
                      {search ? (
                        <Trans>No devices found</Trans>
                      ) : (
                        <Trans>No devices connected yet</Trans>
                      )}
                    </p>
                  </td>
                </tr>
              )}
              {parents.map((device) => (
                <DeviceRow
                  key={device.id}
                  device={device}
                  // When searching, render flat (no recursive children)
                  allDevices={isSearching ? [] : filteredDevices}
                  level={0}
                  typeMap={typeMap}
                  getTypeIcon={getTypeIcon}
                  onEdit={(d) =>
                    navigate({
                      to: '/devices/$deviceId/edit',
                      params: { deviceId: d.id },
                    })
                  }
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  allDevices,
  level,
  typeMap,
  getTypeIcon,
  onEdit,
  onDelete,
}: {
  device: Device;
  allDevices: Device[];
  level: number;
  typeMap: Record<number, string>;
  getTypeIcon: (typeId: number) => ReactNode;
  onEdit: (device: Device) => void;
  onDelete: (device: Device) => void;
}) {
  const children = allDevices.filter((d) => d.parent === device.id);

  return (
    <>
      <tr className="hover:bg-muted/30 transition-colors">
        <td className="px-6 py-3.5 whitespace-nowrap">
          <div className="flex items-center gap-3" style={{ paddingLeft: `${level * 24}px` }}>
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
              {getTypeIcon(device.deviceType)}
            </div>
            <div>
              <div className="font-medium text-sm">{device.name}</div>
              {level > 0 && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  <Trans>Sub-device</Trans>
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-6 py-3.5 whitespace-nowrap text-sm text-muted-foreground">
          {typeMap[device.deviceType] ?? String(device.deviceType)}
        </td>
        <td className="px-6 py-3.5 whitespace-nowrap text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(device)}>
                <Trans>Edit configuration</Trans>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(device)}
                className="text-destructive focus:text-destructive"
              >
                <Trans>Remove device</Trans>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
      {children.map((child) => (
        <DeviceRow
          key={child.id}
          device={child}
          allDevices={allDevices}
          level={level + 1}
          typeMap={typeMap}
          getTypeIcon={getTypeIcon}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}
